import { generateUUID } from "../../../../helpers/utils";
import { SURVEY_TYPES } from "../constants/constantsSurveys";

export const isAbortError = (error) => {
  return error?.name === "AbortError" || error === "Fetch Aborted by User.";
};

export const getSafeSurveyType = (type) => {
  const allowedTypes = [
    SURVEY_TYPES.text,
    SURVEY_TYPES.number,
    SURVEY_TYPES.singleChoice,
    SURVEY_TYPES.multipleChoice,
    SURVEY_TYPES.image
  ];

  return allowedTypes.includes(type) ? type : SURVEY_TYPES.text;
};

export const createOption = () => ({
  id: generateUUID(),
  label: "",
  childQuestions: []
});

export const createBaseQuestion = (type, level = 1, displayIndex = null) => {
  const safeType = getSafeSurveyType(type);

  const question = {
    id: generateUUID(),
    uid: generateUUID(),
    type: safeType,
    title: "",
    description: "",
    requiredImage: safeType === SURVEY_TYPES.image,
    shouldValidate: false,
    level,
    displayIndex
  };

  if (
    safeType === SURVEY_TYPES.singleChoice ||
    safeType === SURVEY_TYPES.multipleChoice
  ) {
    question.options = [createOption(), createOption()];
  } else {
    question.options = [];
  }

  return question;
};

export const cloneQuestionTree = (question) => {
  return {
    ...question,
    id: generateUUID(),
    uid: generateUUID(),
    shouldValidate: false,
    options: Array.isArray(question.options)
      ? question.options.map((option) => ({
        ...option,
        id: generateUUID(),
        childQuestions: Array.isArray(option.childQuestions)
          ? option.childQuestions.map((child) => cloneQuestionTree(child))
          : []
      }))
      : []
  };
};

const rebuildQuestionDisplayIndexes = (question, displayIndex, level) => {
  const normalizedOptions = Array.isArray(question.options)
    ? question.options.map((option, optionIndex) => ({
      ...option,
      childQuestions: Array.isArray(option.childQuestions)
        ? option.childQuestions.map((childQuestion) =>
          rebuildQuestionDisplayIndexes(
            childQuestion,
            `${displayIndex}.${optionIndex + 1}`,
            level + 1
          )
        )
        : []
    }))
    : [];

  return {
    ...question,
    level,
    displayIndex,
    options: normalizedOptions
  };
};

export const rebuildDisplayIndexes = (
  questions,
  parentDisplayIndex = null,
  level = 1
) => {
  return questions.map((question, questionIndex) => {
    const currentDisplayIndex = parentDisplayIndex
      ? `${parentDisplayIndex}.${questionIndex + 1}`
      : `${questionIndex + 1}`;

    return rebuildQuestionDisplayIndexes(question, currentDisplayIndex, level);
  });
};

const optionHasChildQuestion = (option) =>
  Array.isArray(option?.childQuestions) && option.childQuestions.length > 0;

export const findFirstConditionalQuestionWithoutChildren = (questions = []) => {
  for (const question of questions) {
    const options = question?.options || [];

    if (question?.isConditionalParent && !options.some(optionHasChildQuestion)) {
      return question;
    }

    for (const option of options) {
      const nestedResult = findFirstConditionalQuestionWithoutChildren(
        option?.childQuestions || []
      );

      if (nestedResult) {
        return nestedResult;
      }
    }
  }

  return null;
};

export const hasValidationErrors = (questions) => {
  for (const question of questions) {
    if (!question.title || question.title.trim() === "") {
      return true;
    }

    if (Array.isArray(question.options)) {
      for (const option of question.options) {
        if (!option.label || option.label.trim() === "") {
          return true;
        }

        if (
          Array.isArray(option.childQuestions) &&
          option.childQuestions.length > 0 &&
          hasValidationErrors(option.childQuestions)
        ) {
          return true;
        }
      }
    }
  }

  return false;
};

export const markQuestionsForValidation = (questions) => {
  return questions.map((question) => ({
    ...question,
    shouldValidate: true,
    options: Array.isArray(question.options)
      ? question.options.map((option) => ({
        ...option,
        childQuestions: Array.isArray(option.childQuestions)
          ? markQuestionsForValidation(option.childQuestions)
          : []
      }))
      : []
  }));
};

export const mapOptionsToPayload = (options = []) => {
  return options.map((option) => ({
    id: option.id,
    text: option.label || "",
    selected: false,
    editable: false
  }));
};

export const flattenQuestionsToSurveys = (
  questions,
  parentSurveyUid = null,
  parentOptionId = null,
  acc = []
) => {
  for (const question of questions) {
    const imageRequirement =
      question.type === SURVEY_TYPES.image || question.requiredImage
        ? "REQUIRED"
        : "NO_IMAGE";

    acc.push({
      uid: question.uid,
      id: question.id,
      type: question.type,
      question: question.title || "",
      descriptions: question.description || "",
      response: "",
      image_requirement: imageRequirement,
      parent_survey_uid: parentSurveyUid,
      parent_option_id: parentOptionId,
      level: question.level || 1,
      display_index: question.displayIndex || null,
      options: mapOptionsToPayload(question.options || [])
    });

    if (Array.isArray(question.options)) {
      for (const option of question.options) {
        if (Array.isArray(option.childQuestions) && option.childQuestions.length > 0) {
          flattenQuestionsToSurveys(
            option.childQuestions,
            question.uid,
            option.id,
            acc
          );
        }
      }
    }
  }

  return acc;
};

export const createQuestionFromSurvey = (survey) => {
  const safeType = getSafeSurveyType(survey.type);

  return {
    id: generateUUID(),
    uid: survey.uid || generateUUID(),
    type: safeType,
    title: survey.question || "",
    description: survey.descriptions || "",
    requiredImage:
      safeType === SURVEY_TYPES.image ||
      (typeof survey.image_requirement === "string"
        ? survey.image_requirement !== "NO_IMAGE" &&
        survey.image_requirement.trim() !== ""
        : Boolean(survey.image_requirement)),
    shouldValidate: false,
    level: survey.level || 1,
    displayIndex: survey.display_index || null,
    options: Array.isArray(survey.options)
      ? survey.options.map((option) => ({
        id: option.id || generateUUID(),
        label: option.text || "",
        childQuestions: []
      }))
      : []
  };
};

export const inflateSurveysToQuestions = (surveys = []) => {
  if (!Array.isArray(surveys) || surveys.length === 0) {
    return [];
  }

  const normalizeOption = (option = {}) => ({
    id: option.id || generateUUID(),
    label: option.label ?? option.text ?? "",
    childQuestions: []
  });

  const normalizeSurveyNode = (survey = {}) => {
    const safeType = getSafeSurveyType(survey.type);

    return {
      id: survey.id || generateUUID(),
      uid: survey.uid || generateUUID(),
      type: safeType,
      title: survey.question || "",
      description: survey.descriptions || "",
      requiredImage:
        safeType === SURVEY_TYPES.image ||
        (typeof survey.image_requirement === "string"
          ? survey.image_requirement !== "NO_IMAGE" &&
            survey.image_requirement.trim() !== ""
          : Boolean(survey.image_requirement)),
      shouldValidate: false,
      level: survey.level || 1,
      displayIndex: survey.display_index || survey.displayIndex || null,
      parentOptionId: survey.parent_option_id || survey.parentOptionId || null,
      parentSurveyUid: survey.parent_survey_uid || survey.parentSurveyUid || null,
      isConditionalParent: false,
      options: Array.isArray(survey.options)
        ? survey.options.map(normalizeOption)
        : []
    };
  };

  const nodes = surveys.map(normalizeSurveyNode);

  const rootNodes = nodes.filter((node) => !node.parentSurveyUid);

  const childrenByParentOptionKey = new Map();

  nodes.forEach((node) => {
    if (!node.parentSurveyUid || !node.parentOptionId) {
      return;
    }

    const key = `${node.parentSurveyUid}::${node.parentOptionId}`;

    if (!childrenByParentOptionKey.has(key)) {
      childrenByParentOptionKey.set(key, []);
    }

    childrenByParentOptionKey.get(key).push(node);
  });

  const buildTree = (node) => {
    const builtOptions = (node.options || []).map((option) => {
      const key = `${node.uid}::${option.id}`;
      const childNodes = childrenByParentOptionKey.get(key) || [];

      return {
        ...option,
        childQuestions: childNodes.map((childNode) => buildTree(childNode))
      };
    });

    return {
      ...node,
      options: builtOptions,
      isConditionalParent: builtOptions.some(
        (option) => (option.childQuestions || []).length > 0
      )
    };
  };

  return rootNodes.map((rootNode) => buildTree(rootNode));
};

export const countQuestions = (questions = []) => {
  return questions.reduce((total, question) => {
    const childrenCount = Array.isArray(question.options)
      ? question.options.reduce((optionTotal, option) => {
        return optionTotal + countQuestions(option.childQuestions || []);
      }, 0)
      : 0;

    return total + 1 + childrenCount;
  }, 0);
};

export const countQuestionsWithImages = (questions = []) => {
  return questions.reduce((total, question) => {
    const currentCount =
      question.type === SURVEY_TYPES.image || question.requiredImage ? 1 : 0;

    const childrenCount = Array.isArray(question.options)
      ? question.options.reduce((optionTotal, option) => {
        return optionTotal + countQuestionsWithImages(option.childQuestions || []);
      }, 0)
      : 0;

    return total + currentCount + childrenCount;
  }, 0);
};

export const updateQuestionInTree = (questions, id, updatedFields) => {
  return questions.map((question) => {
    if (question.id === id) {
      return {
        ...question,
        ...updatedFields
      };
    }

    return {
      ...question,
      options: Array.isArray(question.options)
        ? question.options.map((option) => ({
          ...option,
          childQuestions: updateQuestionInTree(
            option.childQuestions || [],
            id,
            updatedFields
          )
        }))
        : []
    };
  });
};

export const deleteQuestionFromTree = (questions, id) => {
  return questions.reduce((acc, question) => {
    if (question.id === id) {
      return acc;
    }

    acc.push({
      ...question,
      options: Array.isArray(question.options)
        ? question.options.map((option) => ({
          ...option,
          childQuestions: deleteQuestionFromTree(option.childQuestions || [], id)
        }))
        : []
    });

    return acc;
  }, []);
};

export const duplicateQuestionInTree = (questions, questionToDuplicate) => {
  return questions.flatMap((question) => {
    if (question.id === questionToDuplicate.id) {
      return [question, cloneQuestionTree(question)];
    }

    return [
      {
        ...question,
        options: Array.isArray(question.options)
          ? question.options.map((option) => ({
            ...option,
            childQuestions: duplicateQuestionInTree(
              option.childQuestions || [],
              questionToDuplicate
            )
          }))
          : []
      }
    ];
  });
};